import React, { useMemo } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { Box, Text, colors, dimensions } from '@factorialco/deprecated-design-system'
import { Bar, BarCustomLayerProps } from '@nivo/bar'

import i18n from 'modules/core/lib/i18n'

import { JobCatalogGenderData } from 'modules/job_catalog/components/JobCatalogPeopleCharts/models'
import { generateTickValues } from 'modules/job_catalog/components/JobCatalogPeopleCharts/utils/chart'

interface JobCatalogGenderChartProps {
  data: JobCatalogGenderData[]
  keys: string[]
  count: number
  malePercentage: number
  femalePercentage: number
  notDeclaredPercentage: number
  groupedBy: string
}

export const JobCatalogGenderChart = ({
  data,
  keys,
  count,
  malePercentage,
  femalePercentage,
  notDeclaredPercentage,
  groupedBy,
}: JobCatalogGenderChartProps) => {
  const tickValues = useMemo(() => generateTickValues(data, keys), [data, keys])
  const customGridLayer = ({ yScale, innerWidth }: BarCustomLayerProps<JobCatalogGenderData>) => {
    return tickValues.map((value) => {
      const y = yScale(value)
      return (
        <line
          key={value}
          x1={0}
          x2={innerWidth}
          y1={y}
          y2={y}
          stroke={colors.grey400}
          strokeWidth={1}
          strokeDasharray="4 4"
        />
      )
    })
  }

  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
      padding="s24"
      width="full"
    >
      <Box flexDirection="row" gap="s4">
        <Text.Normal weight="medium" color="grey1000">
          {i18n.t('job_catalog.nodes.people.charts.gender.title')}
        </Text.Normal>
        {groupedBy && (
          <Text.Normal color={'grey800'}>
            · {i18n.t('job_catalog.nodes.people.charts.gender.by', { nodeType: groupedBy })}
          </Text.Normal>
        )}
      </Box>
      <Box flexDirection="row" gap="s80" paddingTop="s24" paddingBottom="s24">
        <Box flexDirection="column">
          <Text.Header1>{count}</Text.Header1>
          <Text.Normal color="grey700">
            {i18n.t('job_catalog.nodes.people.charts.gender.people')}
          </Text.Normal>
        </Box>
        <Box flexDirection="column">
          <Text.Header1>{malePercentage}%</Text.Header1>
          <Text.Normal color="grey700">
            {i18n.t('job_catalog.nodes.people.charts.gender.male')}
          </Text.Normal>
        </Box>
        <Box flexDirection="column">
          <Text.Header1>{femalePercentage}%</Text.Header1>
          <Text.Normal color="grey700">
            {i18n.t('job_catalog.nodes.people.charts.gender.female')}
          </Text.Normal>
        </Box>
        <Box flexDirection="column">
          <Text.Header1>{notDeclaredPercentage}%</Text.Header1>
          <Text.Normal color="grey700">
            {i18n.t('job_catalog.nodes.people.charts.gender.notDeclared')}
          </Text.Normal>
        </Box>
      </Box>
      <Box height="s240" width="full">
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <Bar
              height={height}
              width={width}
              data={data}
              indexBy="id"
              keys={keys}
              margin={{ top: 20, right: 10, bottom: 70, left: 30 }}
              padding={0.3}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={[colors.viridian1000, colors.purple1000, colors.fuchsia1000]}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 1.6]],
              }}
              borderRadius={4}
              layers={[customGridLayer, 'axes', 'bars', 'markers', 'legends', 'annotations']}
              axisTop={null}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickValues: tickValues,
              }}
              axisRight={null}
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{
                from: 'color',
                modifiers: [['darker', 1.6]],
              }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-left',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: 70,
                  itemsSpacing: 5,
                  itemWidth: 65,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 10,
                  symbolShape: 'circle',
                },
              ]}
              theme={{
                axis: {
                  ticks: {
                    line: {
                      strokeWidth: 1,
                      stroke: colors.grey400,
                    },
                    text: {
                      color: colors.grey800,
                      fontSize: dimensions.s12,
                      fontFamily: 'Inter',
                    },
                  },
                  legend: {
                    text: {
                      fontFamily: 'Inter',
                    },
                  },
                },
                legends: {
                  text: {
                    fontFamily: 'Inter',
                    fontSize: 12,
                  },
                },
              }}
              enableGridY={false}
              enableGridX={false}
              animate
              motionConfig="gentle"
            />
          )}
        </AutoSizer>
      </Box>
    </Box>
  )
}
